import { Box, Icon, SxProps, visuallyHidden } from "@outschool/backpack";
import {
  IconDefinition,
  faCalendarAlt,
  faChalkboardTeacher,
  faChartLine,
  faChevronDown,
  faChevronUp,
  faHeart,
  faQuestionCircle,
  faSignIn,
  faUsersCog
} from "@outschool/icons";
import {
  BASE_LOCALE,
  TFunction,
  useLocale,
  useTranslation
} from "@outschool/localization";
import {
  leadActivitiesPath,
  savedClassesPath,
  sellerOrgPath,
  teacherDashboardPath,
  teacherSchedulePath
} from "@outschool/routes";
import { useSession } from "@outschool/ui-auth";
import { UserHeadshotImage } from "@outschool/ui-components-shared";
import { NavHelpLink } from "@outschool/ui-components-website";
import {
  Popover,
  useImpressionTracking
} from "@outschool/ui-legacy-component-library";
import {
  useClickOutsideRefEventListener,
  useEventListener
} from "@outschool/ui-utils";
import React, { PropsWithChildren } from "react";

import { FeatureFlag } from "../../../shared/Env";
import * as Routes from "../../../shared/Routes";
import { useHasEnrolled, usePricingOfferAdmin } from "../../hooks/CurrentUser";
import { useUserWalletSummary } from "../../hooks/useUserWalletSummary";
import { isBuyerOrgAdmin } from "../../lib/BuyerOrgAdmins";
import { useFeatureFlag } from "../../lib/FeatureFlags";
import { UserMode, useUserMode } from "../../lib/UserMode";
import JoinAndWelcomeButton from "../JoinAndWelcomeButton";
import Link from "../Link";
import HeaderMenu from "./HeaderMenu";
import HeaderNavItem from "./HeaderNavItem";
import { EducatorModeToolbar } from "./TeachToolbar";
import { WalletNavItem } from "./WalletNavItem";

export default function DesktopHeaderNav() {
  const { isLoggedIn, isLeader, currentUser } = useSession();

  // Show Manage link for seller org owners
  const isSellerOrg = !!currentUser?.sellerOrg?.currentUserIsOwner;

  const {
    totalBalanceCents: walletBalance,
    loading: walletBalanceLoading,
    pricingOffers
  } = useUserWalletSummary();
  const showWalletFunds =
    (walletBalance > 0 || pricingOffers.length > 0) && !walletBalanceLoading;

  // If user is buyer org admin and FF enabled, hide all nav items
  const buyerOrgAdminModeEnabled = useFeatureFlag(
    FeatureFlag.BuyerOrgAdminMode
  );
  const isBuyerOrgAdminMode =
    buyerOrgAdminModeEnabled && isBuyerOrgAdmin(currentUser);

  const { t } = useTranslation("client\\components\\nav\\DesktopHeaderNav");
  const innerProps = {
    isLeader,
    isSellerOrg,
    isOrgApplicant: !!currentUser?.latestSellerOrgApplication,
    showWalletFunds,
    isBuyerOrgAdminMode,
    t
  };
  if (isLoggedIn) {
    return (
      <Box
        data-test-id="logged-in-nav"
        flex
        sx={{ flex: 2, justifyContent: "flex-end" }}
      >
        <LoggedInNav {...innerProps} />
      </Box>
    );
  } else {
    return <AnonymousNav t={t} />;
  }
}

/** Header navigation for non-logged in users */
function AnonymousNav({ t }: { t: TFunction }) {
  return (
    <NavItems
      sx={{
        gap: "1.25em"
      }}
    >
      <HelpLink t={t} hideLabel={false} />
      <LoginLink t={t} />
      <JoinAndWelcomeButton>{t`Join`}</JoinAndWelcomeButton>
    </NavItems>
  );
}

/** Header navigation for logged in users, when the educator mode feature flag is off */
function LoggedInNav({
  isLeader,
  isSellerOrg,
  isOrgApplicant,
  showWalletFunds,
  isBuyerOrgAdminMode,
  t
}) {
  // For parents, always show Schedule link
  // For teachers, query if they have enrolled
  const showScheduleLink = useHasEnrolled({ skip: !isLeader });
  const pricingOfferAdminOrgUid = usePricingOfferAdmin();

  if (!isSellerOrg && !isBuyerOrgAdminMode) {
    return (
      <UserModeNav
        isLeader={isLeader}
        isSellerOrg={isSellerOrg}
        showScheduleLink={showScheduleLink}
        pricingOfferAdminOrgUid={pricingOfferAdminOrgUid}
        showWalletFunds={showWalletFunds}
        t={t}
      />
    );
  } else if (isBuyerOrgAdminMode) {
    // The empty box is used to ensure the menu popover properly handles overflow
    return (
      <Box
        flex
        sx={{
          paddingY: "6px"
        }}
      >
        <Box
          sx={{
            marginLeft: "50px"
          }}
        />
        <DesktopHeaderNavUserDropdown label={t("Admin")} />
      </Box>
    );
  } else {
    return (
      <NavItems>
        <FavoritesLink t={t} />
        {isOrgApplicant && <SellerOrgManageLink t={t} />}
        {isLeader && (
          <TeachLink isLeader={isLeader} isSellerOrg={isSellerOrg} t={t} />
        )}
        {showScheduleLink && <ScheduleLink t={t} />}
        {pricingOfferAdminOrgUid && (
          <WalletDashboardLink
            organizationUid={pricingOfferAdminOrgUid}
            t={t}
          />
        )}
        {showWalletFunds && <WalletNavItem />}
        <DesktopHeaderNavUserDropdown />
      </NavItems>
    );
  }
}

/**
 * Header navigation for logged in users when the educator mode feature is enabled.
 * Certain elements are shown only in Educator mode or Parent mode.
 */
function UserModeNav({
  isSellerOrg,
  showScheduleLink,
  pricingOfferAdminOrgUid,
  showWalletFunds = true,
  isLeader,
  t
}) {
  const locale = useLocale();
  const userMode = useUserMode();

  // A few components are shared between modes.
  const scheduleLink = showScheduleLink && <ScheduleLink t={t} />;

  // Only show the "Switch to ..." button for users that are both teachers and parents.
  if (userMode === UserMode.Educator) {
    return (
      <>
        <EducatorModeToolbar />
        <NavItems sx={{ gap: "1em" }}>
          {isSellerOrg && <SellerOrgManageLink t={t} />}
          <SwitchModeButton
            userMode={userMode}
            isSellerOrg={isSellerOrg}
            t={t}
          />
          {pricingOfferAdminOrgUid && (
            <WalletDashboardLink
              organizationUid={pricingOfferAdminOrgUid}
              t={t}
            />
          )}
          <DesktopHeaderNavUserDropdown />
        </NavItems>
      </>
    );
  } else {
    return (
      <NavItems sx={{ gap: "1em" }}>
        <FavoritesLink t={t} />
        {isLeader && (
          <SwitchModeButton
            userMode={userMode}
            isSellerOrg={isSellerOrg}
            t={t}
          />
        )}

        {scheduleLink}
        {pricingOfferAdminOrgUid && (
          <WalletDashboardLink
            organizationUid={pricingOfferAdminOrgUid}
            t={t}
          />
        )}
        {showWalletFunds && <WalletNavItem />}
        <HelpLink hideLabel={locale === BASE_LOCALE} t={t} />
        <DesktopHeaderNavUserDropdown />
      </NavItems>
    );
  }
}

function FavoritesLink({ t }: { t: TFunction }) {
  return (
    <Link to={savedClassesPath()} trackingName="nav_favorites">
      <HeaderNavItem sx={{ paddingLeft: 0 }}>
        <HeaderNavLabel icon={faHeart} label={t("Favorites")} />
      </HeaderNavItem>
    </Link>
  );
}

function TeachLink({
  isLeader,
  isSellerOrg,
  t
}: {
  isLeader: boolean;
  isSellerOrg: boolean;
  t: TFunction;
}) {
  return (
    <Link
      to={
        isSellerOrg
          ? teacherSchedulePath()
          : isLeader
          ? teacherDashboardPath()
          : leadActivitiesPath()
      }
      trackingName="nav_teach_link"
    >
      <HeaderNavItem>
        <HeaderNavLabel icon={faChalkboardTeacher} label={t("Teach")} />
      </HeaderNavItem>
    </Link>
  );
}

function SellerOrgManageLink({ t }: { t: TFunction }) {
  return (
    <Link to={sellerOrgPath()} trackingName="nav_manage_link">
      <HeaderNavItem>
        <HeaderNavLabel icon={faUsersCog} label={t("Manage")} />
      </HeaderNavItem>
    </Link>
  );
}

function HelpLink({
  hideLabel = false,
  t
}: {
  hideLabel: boolean;
  t: TFunction;
}) {
  return (
    <NavHelpLink fullWidth={false}>
      <HeaderNavItem>
        <HeaderNavLabel
          icon={faQuestionCircle}
          label={t("Help")}
          hideLabel={hideLabel}
        />
      </HeaderNavItem>
    </NavHelpLink>
  );
}

function LoginLink({ t }: { t: TFunction }) {
  return (
    <Link to={Routes.loginPath()} trackingName="nav_login_link">
      <HeaderNavItem>
        <HeaderNavLabel icon={faSignIn} label={t("Log In")} />
      </HeaderNavItem>
    </Link>
  );
}

function ScheduleLink({ t }: { t: TFunction }) {
  return (
    <Link to={Routes.learnSchedulePath()} trackingName="nav_schedule_link">
      <HeaderNavItem>
        <HeaderNavLabel icon={faCalendarAlt} label={t("Schedule")} />
      </HeaderNavItem>
    </Link>
  );
}

function WalletDashboardLink({
  organizationUid,
  t
}: {
  organizationUid: string;
  t: TFunction;
}) {
  return (
    <Link
      // @ts-ignore TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
      to={Routes.pricingOfferAdminDashboardPath(null, organizationUid)}
      trackingName="nav_pricing_offer_dashbaord_topbar"
    >
      <HeaderNavItem>
        <HeaderNavLabel icon={faChartLine} label={t("Dashboard")} />
      </HeaderNavItem>
    </Link>
  );
}

export const PopoverMenu: React.FC<
  PropsWithChildren<{
    open: boolean;
    setOpen: (open: boolean) => void;
    renderButton: (
      ref: React.Ref<HTMLElement>,
      buttonRef: React.Ref<HTMLElement>
    ) => React.ReactElement;
    buttonRef: React.MutableRefObject<HTMLElement>;
  }>
> = props => {
  const popoverRef = React.useRef<HTMLElement>(null);

  const hide = (event: Event) => {
    const el = document.getElementById("localization-picker-menu");
    const target = event.target as HTMLButtonElement;
    if (!el?.contains(target)) {
      props.setOpen(false);
    }
  };
  useClickOutsideRefEventListener(
    document,
    [popoverRef, props.buttonRef],
    hide
  );
  useEventListener(document, "keyup", (event: Event) => {
    if (event["key"] === "Escape") {
      hide(event);
    }
  });

  return (
    <Popover
      ref={popoverRef}
      renderButton={ref => props.renderButton(ref, props.buttonRef)}
      isHidden={!props.open}
    >
      {props.children}
    </Popover>
  );
};

export function DesktopHeaderNavUserDropdown({ label }: { label?: string }) {
  const { t } = useTranslation("client\\components\\nav\\DesktopHeaderNav");
  const [numCloseCount, setNumCloseCount] = React.useState(0);
  const [open, setOpen] = React.useState(false);
  const desktopHeaderNavUserDropdownNode = React.useRef<HTMLElement>();
  const { trackTouch: trackDesktopHeaderNavUserDropdownTouch } =
    useImpressionTracking({
      node: desktopHeaderNavUserDropdownNode.current,
      trackingLabel: "desktop_header_menu_open_button",
      uniqueId: `desktop_header_menu_open_button-${numCloseCount}`,
      sharedProperties: {
        numCloseCount
      }
    });
  const { currentUser } = useSession();

  return (
    <PopoverMenu
      open={open}
      setOpen={(openBool: boolean) => {
        if (!openBool) {
          setNumCloseCount(numCloseCount + 1);
        }
        setOpen(openBool);
      }}
      // @ts-ignore TS(2322): Type 'MutableRefObject<HTMLElement | undefined>' i... Remove this comment to see the full error message
      buttonRef={desktopHeaderNavUserDropdownNode}
      renderButton={(ref, buttonRef) => {
        return (
          <Box ref={buttonRef}>
            <HeaderNavItem
              sx={{ paddingY: "0.5em" }}
              onClick={() => {
                if (open) {
                  setNumCloseCount(numCloseCount + 1);
                } else {
                  trackDesktopHeaderNavUserDropdownTouch();
                }
                setOpen(!open);
              }}
              ref={ref as React.Ref<HTMLDivElement>}
            >
              {/* @ts-ignore TS(2322): Type '{ __typename?: "User" | undefined; uid: stri... Remove this comment to see the full error message */}
              <UserHeadshotImage size={32} user={currentUser} />
              {label && (
                <Box
                  sx={{
                    marginLeft: "0.5em",
                    color: "grey.500"
                  }}
                >
                  {label}
                </Box>
              )}
              <Icon
                icon={open ? faChevronUp : faChevronDown}
                sx={{
                  marginLeft: "0.5em"
                }}
              />
              <span style={visuallyHidden}>{t`Menu`}</span>
            </HeaderNavItem>
          </Box>
        );
      }}
    >
      <HeaderMenu onClose={() => setOpen(false)} />
    </PopoverMenu>
  );
}

export function HeaderNavLabel({
  label,
  icon,
  iconSx = {},
  hideLabel
}: {
  label: string;
  icon?: IconDefinition;
  iconSx?: SxProps;
  hideLabel?: boolean;
}) {
  return (
    <Box
      flex
      sx={{
        alignItems: "center"
      }}
      aria-label={label}
    >
      {icon && (
        <Icon
          icon={icon}
          sx={[
            {
              fontSize: "1.333em",
              marginRight: hideLabel ? "none" : "0.5em"
            },
            ...(Array.isArray(iconSx) ? iconSx : [iconSx])
          ]}
        />
      )}
      {!hideLabel && label}
    </Box>
  );
}

function NavItems({
  sx,
  children
}: {
  sx?: SxProps;
  children: React.ReactNode[];
}) {
  return (
    <Box
      flex
      sx={{
        gap: "0.5em",
        paddingY: "6px",
        "a:hover:not(:disabled),a:focus:not(:disabled)": {
          textDecoration: "none"
        },
        flex: 1,
        justifyContent: "end",
        alignItems: "center",
        ...sx
      }}
    >
      {children}
    </Box>
  );
}

interface SwitchModeButtonProps {
  userMode: UserMode;
  isSellerOrg: boolean;
  t: TFunction;
}

function SwitchModeButton({ userMode, isSellerOrg, t }: SwitchModeButtonProps) {
  return userMode === UserMode.Educator ? (
    <Link to={Routes.parentRootPath()} trackingName="nav_parent_link">
      <HeaderNavItem>{t`Switch to Parent`}</HeaderNavItem>
    </Link>
  ) : (
    <TeachLink isLeader={true} isSellerOrg={isSellerOrg} t={t} />
  );
}
